<script>
import lazyLoadComponent from '@router/lazyload-component'
import { authComputed, merchantComputed } from '@state/helpers'

import Layout from '@layouts/main'
import CheckInBranchSelect from './components/CheckInBranchSelect.vue'

const BRANCH_STORAGE_PREFIX = 'schedule-check-in:branchProductId:'

export default {
  page: {
    title: '數據分析',
    meta: [
      { name: 'description', container: 'Data Analytics' },
      {
        name: 'google-signin-scope',
        content: 'https://www.googleapis.com/auth/analytics.readonly',
      },
    ],
  },
  components: {
    Layout,
    CheckInBranchSelect,
  },
  props: {
    staffItems: {
      type: Array,
      required: true,
    },
    merchantProducts: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 'cosmetic',
      formattedJson: '',
      tabList: [
        {
          tabName: 'cosmetic',
          title: '美容服務預約',
          component: () => lazyLoadComponent(import('./components/checkInList.vue')),
          type: 'component',
          fetchType: 'cosmetic',
          isSpace: false,
          fetchTypes: ['cosmetic'],
          showInIpetDingBeauty: true,
          permissions: true,
        },
      ],
      filterLocationList: [],
      locationValue: '',
      appStyle: process.env.VUE_APP_BUILD_MODE === 'admin-app-build',
      cosmeticSettingData: {},
    }
  },
  computed: {
    ...authComputed,
    ...merchantComputed,
    visibleTabList() {
      return (this.tabList || []).filter((tab) => {
        if (tab.permissions === undefined) return true
        if (typeof tab.permissions === 'boolean') return tab.permissions
        if (typeof tab.permissions === 'string') return this.currentUserRoutes && this.currentUserRoutes.includes(tab.permissions)
        return Boolean(tab.permissions)
      })
    },
    /** 分頁標籤列（含 showInIpetDingBeauty），避免 template 上 v-for 與 v-if 並用 */
    visibleTabPaneList() {
      return this.visibleTabList.filter((tab) => tab.showInIpetDingBeauty && tab.permissions)
    },
    currentProduct() {
      if (!this.filterLocationList) return {}
      return this.filterLocationList.find((item) => item.id === this.locationValue) || {}
    },
  },
  watch: {
    locationValue(newId) {
      if (newId) {
        this.$store.commit('hotel/setting/SET_PRODUCT', this.currentProduct)
      }
      this.persistBranchSelection(newId)
    },
    activeTab() {
      this.syncHashFromActiveTab()
      if (this.activeTab === 'hotel') return
      this.$nextTick(() => {
        if (this.locationValue) {
          this.syncBranchToActiveCheckInList()
        }
      })
    },
    '$route.hash'(hash) {
      this.applyHashFromRouteString(hash)
    },
    visibleTabList: {
      handler(list) {
        const tabNames = (list || []).map((t) => t.tabName)
        if (tabNames.length && !tabNames.includes(this.activeTab)) {
          this.activeTab = tabNames[0]
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.initModuleTab()
    this.applyRouteHashToActiveTab()
    this.seedFilterLocationsForHotelTab()
    this.restoreLocationValueFromStorage()
    await this.getService()
  },
  activated() {
    this.applyRouteHashToActiveTab()
    this.seedFilterLocationsForHotelTab()
    this.restoreLocationValueFromStorage()
  },
  methods: {
    tabNamesInVisibleList() {
      return (this.visibleTabList || []).map((t) => t.tabName)
    },
    /**
     * 初次進入或從外部帶 hash（如 #hotel）時切到對應分頁
     */
    applyRouteHashToActiveTab() {
      const raw = (this.$route.hash || '').replace(/^#/, '')
      if (!raw) return
      const tabNames = this.tabNamesInVisibleList()
      if (tabNames.includes(raw)) {
        this.activeTab = raw
      }
    },
    /**
     * 瀏覽器上一頁/下一頁或程式改 hash 時同步分頁
     */
    applyHashFromRouteString(hash) {
      const raw = (hash || '').replace(/^#/, '')
      const tabNames = this.tabNamesInVisibleList()
      const firstName = tabNames[0]
      if (!raw) {
        if (firstName && this.activeTab !== firstName) {
          this.activeTab = firstName
        }
        return
      }
      if (tabNames.includes(raw) && this.activeTab !== raw) {
        this.activeTab = raw
      }
    },
    /**
     * 第一個可見分頁不寫入 hash（維持乾淨預設 URL），其餘以 #tabName 深連結
     */
    syncHashFromActiveTab() {
      const tabNames = this.tabNamesInVisibleList()
      if (!tabNames.length || !tabNames.includes(this.activeTab)) return
      const firstName = tabNames[0]
      const targetHash = this.activeTab === firstName ? '' : `#${this.activeTab}`
      const current = this.$route.hash || ''
      if (current === targetHash) return
      this.$router.replace({
        path: this.$route.path,
        query: this.$route.query,
        hash: targetHash,
      })
    },
    branchStorageKey() {
      const id = this.currentMerchant && this.currentMerchant.id
      return id != null && id !== '' ? `${BRANCH_STORAGE_PREFIX}${id}` : null
    },
    readSavedBranchId() {
      const key = this.branchStorageKey()
      if (!key || typeof localStorage === 'undefined') return ''
      try {
        return localStorage.getItem(key) || ''
      } catch {
        return ''
      }
    },
    persistBranchSelection(productId) {
      const key = this.branchStorageKey()
      if (!key || typeof localStorage === 'undefined') return
      try {
        if (productId) {
          localStorage.setItem(key, String(productId))
        } else {
          localStorage.removeItem(key)
        }
      } catch {
        // ignore quota / private mode
      }
    },
    /**
     * 將目前選中的分店同步到當前分頁的 checkInList（住宿分頁依 currentProduct，不需此方法）
     */
    syncBranchToActiveCheckInList() {
      if (this.activeTab === 'hotel') return
      const step = this.$refs[`step${this.activeTab}`]
      const ref = step && (Array.isArray(step) ? step[0] : step)
      if (ref && typeof ref.updateFilterFromFather === 'function') {
        ref.updateFilterFromFather(this.locationValue)
      }
    },
    showOffOuterTab() {
      for (let i = 0, len = this.$refs.outerTabs.$children[0].$refs.tabs.length; i < len; i++) {
        this.$refs.outerTabs.$children[0].$refs.tabs[i].style.display = 'none'
      }
    },
    /**
     * 僅開住宿分頁時不會掛載 checkInList，無 getProductList；用路由已載入的商家服務地點補上選單選項
     */
    seedFilterLocationsForHotelTab() {
      if (this.filterLocationList && this.filterLocationList.length) return
      if (this.activeTab !== 'hotel') return
      const list = this.merchantProducts
      if (!Array.isArray(list) || !list.length) return
      this.filterLocationList = list.map((p) => ({
        id: p.id,
        title: p.title != null ? p.title : p.name != null ? p.name : String(p.id),
      }))
    },
    restoreLocationValueFromStorage() {
      if (this.locationValue) return
      const savedId = this.readSavedBranchId()
      if (!savedId || !this.filterLocationList.length) return
      const matched = this.filterLocationList.find((item) => String(item.id) === String(savedId))
      if (matched) {
        this.locationValue = matched.id
      }
    },
    getProductList({ filterLocationList }) {
      this.filterLocationList = filterLocationList
      const prevEmpty = !this.locationValue
      this.restoreLocationValueFromStorage()
      if (prevEmpty && this.locationValue) {
        this.$nextTick(() => {
          this.syncBranchToActiveCheckInList()
        })
      }
    },
    /**
     * @description 更動服務地點 -> 重新撈資料
     */
    changeLocation() {
      this.syncBranchToActiveCheckInList()
    },
    resetProduct() {
      this.locationValue = ''
    },
    /**
     * @description 初始化module
     */
    initModuleTab() {
      if (this.$hasDayCare()) {
        this.tabList.push({
          tabName: 'daycare',
          title: '安親住宿預約',
          component: () => lazyLoadComponent(import('./components/checkInList.vue')),
          type: 'component',
          fetchType: 'daycare',
          fetchTypes: ['daycare'],
          showInIpetDingBeauty: true,
          permissions: true,
        })
      }
      if (this.$hasSpaceModule()) {
        this.tabList.push({
          tabName: 'space',
          title: '空間管理',
          component: () => lazyLoadComponent(import('./components/checkInList.vue')),
          type: 'component',
          fetchType: 'cosmetic',
          isSpace: true,
          fetchTypes: ['space'],
          showInIpetDingBeauty: true,
          permissions: true,
        })
      }
      if (this.$hasHotel()) {
        this.tabList.push({
          tabName: 'hotel',
          title: '寵物住宿預約',
          component: () => lazyLoadComponent(import('./hotel/hotelIndex.vue')),
          type: 'component',
          fetchType: 'hotel',
          isSpace: false,
          fetchTypes: ['hotel'],
          showInIpetDingBeauty: process.env.VUE_APP_DEV_MODE === 'ipetbooking',
          permissions: 'hotel',
        })
      }
    },
    async getService() {
      const { response } = await this.$store.dispatch('merchant/getCosmeticBasicSet', {
        type: 'cosmetic',
      })
      this.cosmeticSettingData = response.items
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.page">
      <el-tabs ref="outerTabs" v-model="activeTab" stretch type="border-card" class="isStyleBase" :class="$style.showTabPane">
        <el-tab-pane>
          <CheckInBranchSelect
            slot="label"
            v-model="locationValue"
            :options="filterLocationList"
            @change="changeLocation"
          />
        </el-tab-pane>
        <el-tab-pane v-for="tab in visibleTabPaneList" :key="tab.tabName" :label="tab.title" :name="tab.tabName"> </el-tab-pane>
      </el-tabs>
      <div v-for="tab in visibleTabList" :key="tab.tabName" :class="$style.componentMargin">
        <component
          :is="tab.component"
          v-if="activeTab === tab.tabName"
          :ref="`step${tab.tabName}`"
          :merchant-products="merchantProducts"
          :staff-items="staffItems"
          :fetch-type="tab.fetchType"
          :fetch-types="tab.fetchTypes"
          :is-space="tab.isSpace"
          :enable-admin-space-order="cosmeticSettingData.enableAdminSpaceOrder"
          :current-product="currentProduct"
          @getProductList="getProductList"
          @reset="resetProduct"
        >
        </component>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';

:global {
  .isPage {
    .el-tabs__content {
      padding-bottom: 40px;
    }
  }
}

.page {
  :global {
    .el-tab-pane {
      background-color: $color-body-bg !important;
      padding: 20px;
    }

    .el-tabs__item {
      min-width: 152px;
    }
  }
}

.showTabPane {
  :global {
    .el-tabs__content {
      display: none;
    }
  }
}

.componentMargin {
  margin: 20px;
}
</style>
